import React from 'react';
import { usePostControlFromUI } from '../../hooks/usePostControlFromUI';
import { SelectSchemaValid } from '../../validations/locationIsValid';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for routing
import { useConditional } from '../../state/storeConditional ';

type Location = {
	name?: string | undefined;
	method?: string | undefined;
	id?: string | undefined;
	country?: string | undefined;
	city?: string | undefined;
	lat?: string | undefined;
	lng?: string | undefined;
};

type Props = {
	onChange: (value: Location) => void;
	options?: SelectSchemaValid;
	label: string;
	meta: string;
	value: Location;
	placeholder?: string; // Add placeholder prop
};

const Dropdown = ({ options, label, meta, value, onChange, placeholder }: Props) => {
	const { updateIsReadyGenerator } = useConditional();
	const { getSelectedFromUI } = usePostControlFromUI();
	const navigate = useNavigate(); // Initialize useNavigate for routing

	const [open, setOpen] = React.useState(false);

	const selectedOption = (opt: Location) => {
		if (opt.id && typeof opt.country === "string") {
			onChange(opt);
			const newData = { meta, value: opt };
			getSelectedFromUI(newData);
		} else {
			onChange(opt);
			const newData = { meta, value: opt };
			getSelectedFromUI(newData);
		}
		updateIsReadyGenerator(true);
	};

	// Determine the display value
	const displayValue = value?.name || value?.country
		? `${value?.name || value?.country}${value?.city ? ` - ${value.city}` : ''}`
		: placeholder || 'Select an option';

	// Check if the location data is empty
	const isLocationEmpty = !value?.name && !value?.country && !value?.city;

	return (
		<div
			onBlur={() => setOpen(false)}
			tabIndex={0}
			id="select"
			onClick={() => setOpen((prev) => !prev)}
			className="relative"
		>
			<div>{label}</div>
			<div className="bg-white drop-shadow-c-dsh-1 cursor-pointer py-2 h-9 rounded-[4px] w-full text-base text-green-700">
				<div className="flex justify-between px-3 text-center truncate whitespace-nowrap overflow-hidden">
					<span className="truncate whitespace-nowrap overflow-hidden">
						{isLocationEmpty ? (
							<span className="text-gray-400">{placeholder || 'Select an option'}</span>
						) : (
							displayValue
						)}
					</span>
					{open ? <span>🎆</span> : <span>🎇</span>}
				</div>
			</div>
			{open && (
				<div className="absolute drop-shadow-c-dsh-1 px-2 py-2 z-20 mt-2 bg-white w-full">
					{(options?.value ?? []).map((item: Location, index) => (
						<div
							onClick={(e) => {
								e.stopPropagation();
								selectedOption(item);
								setOpen(false);
							}}
							className="py-2 hover:bg-slate-200 px-2"
							key={index}
						>
							{item?.name ? item?.name : `${item?.country} - ${item?.city}`}
						</div>
					))}
					{/* Conditionally render the "Add Custom Location" link */}
					{meta === 'location' && (
						<div
							onClick={(e) => {
								e.stopPropagation();
								navigate('/customizing/location'); // Redirect to the customizing/location route
							}}
							className="py-2 hover:bg-slate-200 px-2 text-blue-500 cursor-pointer"
						>
							+ Add Custom Location
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default Dropdown;